import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminParticipants from "./pages/admin/Participants";
import AdminMessages from "./pages/admin/Messages";
import AdminStats from "./pages/admin/Stats";
import AdminWarnings from "./pages/admin/Warnings";
import AdminBookingPage from "./pages/admin/BookingPage";
import AdminContent from "./pages/admin/ContentManagement";
import AdminSettings from "./pages/admin/Settings";
import AdminCourses from "./pages/admin/CourseManagement";
import AdminUsers from "./pages/admin/UserManagement";
import AdminKVP from "./pages/admin/KVPManagement";
import AdminStatsDashboard from "./pages/admin/StatsDashboard";
import ParticipantDashboard from "./pages/participant/Dashboard";
import ParticipantMessages from "./pages/participant/Messages";
import ParticipantProfile from "./pages/participant/Profile";
import ParticipantContent from "./pages/participant/ContentPage";
import BewerbungsTool from "./pages/participant/BewerbungsTool";
import LernPlattform from "./pages/participant/LernPlattform";
import CourseDetail from "./pages/participant/CourseDetail";
import Community from "./pages/participant/Community";
import Lernraum from "./pages/participant/Lernraum";
import Medienbibliothek from "./pages/participant/Medienbibliothek";
import CourseFeedback from "./pages/participant/CourseFeedback";
import KVP from "./pages/participant/KVP";
import Flashcards from "./pages/participant/Flashcards";
import QMDashboard from "./pages/admin/QMDashboard";
import RightsManagement from "./pages/admin/RightsManagement";
import BookingApproval from "./pages/admin/BookingApproval";
import DozentDashboard from "./pages/admin/DozentDashboard";
import MeisterAgent from "./pages/MeisterAgent";
import Lernportal from "./pages/participant/Lernportal";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles, requiredArea }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // 1) Direct role match
  if (allowedRoles && allowedRoles.includes(user.role)) {
    return children;
  }
  
  // 2) Granular access for dozent/auditor: allow admin-style routes if super-admin
  //    has assigned the required area to this staff member.
  const isStaffRole = ["dozent", "auditor"].includes(user.role);
  const adminGated = allowedRoles && (allowedRoles.includes("admin") || allowedRoles.includes("super_admin"));
  if (isStaffRole && adminGated && requiredArea && user.allowed_areas?.includes(requiredArea)) {
    return children;
  }
  
  // 3) Default redirect by role
  if (["admin", "super_admin"].includes(user.role)) {
    return <Navigate to="/admin" replace />;
  } else if (user.role === "dozent") {
    return <Navigate to="/admin/dozent-dashboard" replace />;
  } else if (user.role === "auditor") {
    return <Navigate to="/admin/qm" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === "auditor") return <Navigate to="/admin/qm" replace />;
    if (user.role === "dozent") return <Navigate to="/admin/dozent-dashboard" replace />;
    if (["admin", "super_admin"].includes(user.role)) return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Admin Routes - accessible by admin, super_admin, and (with granted area) dozent/auditor */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="dashboard"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/participants" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="participants"><AdminParticipants /></ProtectedRoute>} />
      <Route path="/admin/messages" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="messages"><AdminMessages /></ProtectedRoute>} />
      <Route path="/admin/stats" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="statistics"><AdminStats /></ProtectedRoute>} />
      <Route path="/admin/warnings" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="warnings"><AdminWarnings /></ProtectedRoute>} />
      <Route path="/admin/booking/:slug" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="bookings"><AdminBookingPage /></ProtectedRoute>} />
      <Route path="/admin/content" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="content"><AdminContent /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="settings"><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/courses" element={<ProtectedRoute allowedRoles={["admin", "super_admin", "dozent"]} requiredArea="courses"><AdminCourses /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="users"><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/kvp" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="kvp"><AdminKVP /></ProtectedRoute>} />
      <Route path="/admin/statistics" element={<ProtectedRoute allowedRoles={["admin", "super_admin"]} requiredArea="statistics"><AdminStatsDashboard /></ProtectedRoute>} />
      <Route path="/admin/qm" element={<ProtectedRoute allowedRoles={["super_admin", "auditor", "admin"]} requiredArea="qm"><QMDashboard /></ProtectedRoute>} />
      <Route path="/admin/rights" element={<ProtectedRoute allowedRoles={["super_admin"]}><RightsManagement /></ProtectedRoute>} />
      <Route path="/admin/booking-approval" element={<ProtectedRoute allowedRoles={["super_admin", "admin"]} requiredArea="bookings"><BookingApproval /></ProtectedRoute>} />
      
      {/* Participant Routes */}
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["participant"]}><ParticipantDashboard /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute allowedRoles={["participant"]}><ParticipantMessages /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute allowedRoles={["participant"]}><ParticipantProfile /></ProtectedRoute>} />
      <Route path="/content/:slug" element={<ProtectedRoute allowedRoles={["participant"]}><ParticipantContent /></ProtectedRoute>} />
      <Route path="/bewerbung" element={<ProtectedRoute allowedRoles={["participant"]}><BewerbungsTool /></ProtectedRoute>} />
      <Route path="/lernplattform" element={<ProtectedRoute allowedRoles={["participant"]}><LernPlattform /></ProtectedRoute>} />
      <Route path="/lernplattform/:courseId" element={<ProtectedRoute allowedRoles={["participant"]}><CourseDetail /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute allowedRoles={["participant"]}><Community /></ProtectedRoute>} />
      <Route path="/lernraum" element={<ProtectedRoute allowedRoles={["participant"]}><Lernraum /></ProtectedRoute>} />
      <Route path="/medienbibliothek" element={<ProtectedRoute allowedRoles={["participant"]}><Medienbibliothek /></ProtectedRoute>} />
      <Route path="/feedback/:courseId" element={<ProtectedRoute allowedRoles={["participant"]}><CourseFeedback /></ProtectedRoute>} />
      <Route path="/kvp" element={<ProtectedRoute allowedRoles={["participant"]}><KVP /></ProtectedRoute>} />
      <Route path="/lernkarten" element={<ProtectedRoute allowedRoles={["participant"]}><Flashcards /></ProtectedRoute>} />
      
      {/* KI-Assistent - accessible to ALL authenticated roles */}
      <Route path="/admin/dozent-dashboard" element={<ProtectedRoute allowedRoles={["dozent", "admin", "super_admin"]}><DozentDashboard /></ProtectedRoute>} />
      
      {/* S&T Meister-Agent + Lernportal */}
      <Route path="/lernportal" element={<ProtectedRoute allowedRoles={["participant"]}><Lernportal /></ProtectedRoute>} />
      <Route path="/meister-agent" element={<ProtectedRoute allowedRoles={["participant", "admin", "super_admin", "dozent", "auditor"]}><MeisterAgent /></ProtectedRoute>} />
      <Route path="/admin/meister-agent" element={<ProtectedRoute allowedRoles={["admin", "super_admin", "dozent", "auditor"]}><MeisterAgent /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
